import { useEffect, useEffectEvent, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { dialogOpen, isTyping } from './shortcuts.ts'

/** What a list's pager needs: `usePaged`'s result, or a page read in the query. */
export interface ListPage {
  offset: number
  limit: number
  total: number
  setOffset: (offset: number) => void
}

export type ListMove =
  | { select: string }
  /** Turn to the page at `offset`, then select its first or last row. */
  | { turn: number; edge: 'first' | 'last' }
  | null

/**
 * Where an arrow goes: the next row on the page; with nothing selected, ↓ the first row and
 * ↑ the last; past the page's last row the next page (its first row), past its first row
 * the page before (its last row). Null at the very ends.
 */
export function nextMove(
  ids: readonly string[],
  active: string | null,
  by: 1 | -1,
  page?: Pick<ListPage, 'offset' | 'limit' | 'total'>,
): ListMove {
  if (ids.length === 0) return null
  const at = active === null ? -1 : ids.indexOf(active)
  if (at < 0) return { select: by > 0 ? ids[0]! : ids[ids.length - 1]! }
  const next = at + by
  if (next >= 0 && next < ids.length) return { select: ids[next]! }
  if (!page) return null
  if (next >= ids.length && page.offset + page.limit < page.total)
    return { turn: page.offset + page.limit, edge: 'first' }
  if (next < 0 && page.offset > 0)
    return { turn: Math.max(0, page.offset - page.limit), edge: 'last' }
  return null
}

/** The nearest ancestor that scrolls (the app's `<main>`), or the page itself. */
function scrollerOf(el: Element): Element | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const y = getComputedStyle(p).overflowY
    if ((y === 'auto' || y === 'scroll') && p.scrollHeight > p.clientHeight) return p
  }
  return document.scrollingElement
}

/** The list's search box: ↑ ↓ and Enter work from it; everything else stays in the text. */
function isListSearch(target: EventTarget | null): target is HTMLInputElement {
  return target instanceof HTMLInputElement && target.type === 'search'
}

/**
 * The list's keys: the arrows move the selected row (turning the page at either end), Enter
 * opens it, and each letter in `keys` acts on it. The selected row is the one marked
 * `aria-current="true"` inside `list`, and it is scrolled into view after every move (the
 * page's first row with the top of the page, its last row with the bottom). From
 * the list's search box only ↑ ↓ and Enter count. Quiet in other fields, in a dialog, and
 * right after "g" (the go-to shortcuts).
 */
export function useListKeys(handlers: {
  /** The rows on this page, in order. */
  ids: readonly string[]
  active: string | null
  setActive: (id: string) => void
  page?: ListPage
  /** Around the rows, to find the selected one and scroll to it. */
  list: RefObject<HTMLElement | null>
  open: () => void
  keys: Record<string, () => void>
  /** False while another list on the same screen has the keys. */
  enabled?: boolean
}) {
  const { ids, setActive } = handlers
  const shown = ids.join('|')
  // After a page turn: the edge row to select once the new page's rows are here.
  const [pending, setPending] = useState<{ edge: 'first' | 'last'; was: string } | null>(null)
  if (pending && shown !== pending.was && ids.length > 0) {
    setPending(null)
    setActive(pending.edge === 'first' ? ids[0]! : ids[ids.length - 1]!)
  }
  const scrollWanted = useRef(false)
  useLayoutEffect(() => {
    if (!scrollWanted.current || pending) return
    const row = handlers.list.current?.querySelector('[aria-current="true"]')
    if (!row) return
    scrollWanted.current = false
    // The page's first row shows the top of the page too (title, pills, search), its last
    // row the bottom (the pager); a row in between scrolls only as far as it needs.
    const at = handlers.active === null ? -1 : ids.indexOf(handlers.active)
    const scroller = at === 0 || at === ids.length - 1 ? scrollerOf(row) : null
    if (scroller) scroller.scrollTop = at === 0 ? 0 : scroller.scrollHeight
    else row.scrollIntoView({ block: 'nearest' })
  })

  const move = (by: 1 | -1) => {
    const to = nextMove(ids, handlers.active, by, handlers.page)
    if (!to) return
    scrollWanted.current = true
    if ('select' in to) setActive(to.select)
    else {
      setPending({ edge: to.edge, was: shown })
      handlers.page!.setOffset(to.turn)
    }
  }

  const handle = useEffectEvent((e: KeyboardEvent, afterG: boolean) => {
    if (handlers.enabled === false) return
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return
    if (afterG || dialogOpen()) return
    const arrow = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0
    if (isListSearch(e.target)) {
      // Typing the list has not caught up with yet: Enter would open a row of the old results.
      if (arrow) move(arrow)
      else if (e.key === 'Enter' && e.target.dataset.pending === undefined) handlers.open()
      else return
      e.preventDefault()
      return
    }
    if (isTyping(e.target)) return
    // A button or link that has focus keeps Enter for itself.
    const onControl = e.target instanceof HTMLElement && !!e.target.closest('a, button')
    const act = handlers.keys[e.key.toLowerCase()]
    if (arrow) {
      move(arrow)
      // The arrows take the keyboard into the list: a link or button left focused (the
      // menu item just clicked) lets go, so Enter then opens the row, not that link again.
      if (onControl) (e.target as HTMLElement).blur()
    } else if (e.key === 'Enter' && !onControl) handlers.open()
    else if (act) act()
    else return
    e.preventDefault()
  })
  useEffect(() => {
    let gAt = 0
    const onKey = (e: KeyboardEvent) => {
      handle(e, Date.now() - gAt < 1200)
      gAt = e.key === 'g' ? Date.now() : 0
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}
